import React from 'react';
import PropTypes from 'prop-types';
import { useViewMode } from '../../hooks/useViewMode.jsx';
import { useTranslation, localeFor } from '../../utils/translations';
import LanguageToggle from '../Common/LanguageToggle';
import { useSignOutConfirm } from '../Common/ConfirmSignOut';
import { jointParentNames } from './familyCompat';

import Header from '../Layout/Header';
import { FaSignOutAlt } from 'react-icons/fa';
import FamilyToolbar from './FamilyToolbar';
import StudentGroupedView from './StudentGroupedView';
import ActivityGroupedView from './ActivityGroupedView';
import DateGroupedView from './DateGroupedView';
import { LoadingOverlay } from '../Common/LoadingSpinner';
import './DataView.css';

const DataView = ({ phoneNumber, familyData, onBack, isLoading = false }) => {
  const { viewMode } = useViewMode();
  const { t, language } = useTranslation();
  const requestSignOut = useSignOutConfirm(onBack);

  if (!familyData) {
    return (
      <div className="data-view-container">
        <Header />
        <main className="data-view-main">
          {isLoading ? (
            <div className="family-header-card family-header-card-placeholder" aria-hidden="true">
              <div className="header-with-logo">
                <img
                  src="/aos-logo.png"
                  alt=""
                  className="family-logo desktop-logo"
                />
                <img
                  src="/logo.svg"
                  alt=""
                  className="family-logo mobile-logo"
                />
                <div className="family-info">
                  <div className="family-subtitle">{t('main.loadingFamily')}</div>
                </div>
              </div>
            </div>
          ) : (
            <p>{t('main.noFamilyData')}</p>
          )}
        </main>
        {isLoading && <LoadingOverlay message={t('main.loadingFamily')} />}
      </div>
    );
  }

  const { familyId, parentName1, parentName2, children, lastUpdated } = familyData;
  // Shared surname -> "Коля & Люба Андрущенко"; anything unclear -> names as given
  const joint = jointParentNames(parentName1, parentName2);
  // Transform mock data into format expected by grouped views
  const transformDataForViews = (familyData) => {
    const students = familyData.children.map(child => ({
      id: child.childId,
      name: child.studentName,
      birthdate: child.birthdate,
      gender: child.gender,
      activities: [
        {
          id: 'sundaySchool',
          status: child.sundaySchool ? 'completed' : 'pending',
          details: child.sundaySchool
        },
        {
          id: 'russianSchool', 
          status: child.russianSchool ? 'completed' : 'pending',
          details: child.russianSchool
        },
        {
          id: 'childrensChoir',
          status: child.childrensChoir ? 'completed' : 'pending',
          details: child.childrensChoir
        }
      ]
    }));

    const activities = [
      {
        id: 'sundaySchool',
        name: 'Sunday School',
        day: familyData.children[0]?.sundaySchool?.day || 'Wednesday',
        icon: 'FaBook'
      },
      {
        id: 'russianSchool',
        name: 'Russian School',
        day: familyData.children[0]?.russianSchool?.day || 'Saturday',
        icon: 'FaGlobeEurope'
      },
      {
        id: 'childrensChoir',
        name: "Children's Choir",
        day: familyData.children[0]?.childrensChoir?.day || 'Monday',
        icon: 'FaMusic'
      }
    ];

    return { students, activities };
  };

  const { students, activities } = transformDataForViews(familyData);

  return (
    <div className="data-view-container">
      <Header />
      <main className="data-view-main" id="data-to-export">
        <div className="family-header-card">
          <div className="header-with-logo">
            <img 
              src="/aos-logo.png" 
              alt={t('common.logoAlt')} 
              className="family-logo desktop-logo"
            />
            <img 
              src="/logo.svg" 
              alt={t('common.logoAlt')} 
              className="family-logo mobile-logo"
            />
            <div className="family-info">
              <div className="parent-names">
                {joint ? (
                  <>
                    <span className="parent-name">{joint.given1}</span>
                    <span className="name-connector">&</span>
                    <span className="parent-name">
                      {joint.given2} {joint.surname}
                    </span>
                  </>
                ) : (
                  <>
                    <span className="parent-name">{parentName1}</span>
                    {parentName2 && (
                      <>
                        <span className="name-connector">&</span>
                        <span className="parent-name">{parentName2}</span>
                      </>
                    )}
                  </>
                )}
              </div>
              <div className="family-subtitle">{t('common.family')}</div>
            </div>
            <div className="family-language-toggle">
              <LanguageToggle />
              <button
                type="button"
                className="header-signout"
                title={t('main.signOut')}
                onClick={requestSignOut}
              >
                <FaSignOutAlt aria-hidden="true" />
                <span>{t('main.signOut')}</span>
              </button>
            </div>
          </div>
        </div>
        <FamilyToolbar data={{ familyData }} />
        {viewMode === 'student' ? (
          <StudentGroupedView students={students} activities={activities} />
        ) : viewMode === 'activity' ? (
          <ActivityGroupedView students={students} activities={activities} />
        ) : (
          <DateGroupedView students={students} activities={activities} />
        )}
        {lastUpdated && (
          <p className="last-updated-footer">
            {t('main.lastUpdated')}: {new Date(`${lastUpdated}T00:00:00`).toLocaleDateString(localeFor(language))}
          </p>
        )}
        <button type="button" className="signout-bottom" onClick={requestSignOut}>
          <FaSignOutAlt aria-hidden="true" />
          {t('main.signOut')}
        </button>
      </main>
    </div>
  );
};

DataView.propTypes = {
  phoneNumber: PropTypes.string.isRequired,
  familyData: PropTypes.object,
  onBack: PropTypes.func,
  isLoading: PropTypes.bool,
};

export default DataView;
